/**
 * hw-fade-slider — crossfade slider custom element for HW.
 *
 * Wires its own nav arrows directly (looks for [ref="previous"] / [ref="next"]
 * within the parent section) so it does not depend on the carousel-nav block's
 * internal wiring logic. Still exposes the slideshow-component interface so that
 * if the nav block does connect, it works correctly too.
 */

if (!customElements.get('hw-fade-slider')) {
  class HwFadeSlider extends HTMLElement {
    // ── slideshow-component interface ──────────────────────────────────────

    get slides() {
      return [...this.children].filter((el) => el.nodeType === Node.ELEMENT_NODE);
    }

    get current() {
      return this._currentIndex ?? 0;
    }

    get infinite() {
      return true;
    }

    get visibleSlides() {
      const s = this.slides;
      return s.length ? [s[this.current]] : [];
    }

    // ── lifecycle ──────────────────────────────────────────────────────────

    connectedCallback() {
      this._currentIndex = 0;
      this._autoAdvanceTimer = null;
      this._autoAdvanceDelay = null;
      this._navWired = false;

      this._goTo(0, false);
      this._connectNav();

      // Retry a couple of times in case nav renders after us
      setTimeout(() => this._connectNav(), 50);
      setTimeout(() => this._connectNav(), 300);

      this.addEventListener('mouseenter', () => this._stopAutoAdvance());
      this.addEventListener('mouseleave', () => this._resumeAutoAdvance());
      this.addEventListener('focusin', () => this._stopAutoAdvance());
      this.addEventListener('focusout', () => this._resumeAutoAdvance());

      if (this.dataset.autoAdvance === 'true') {
        const ms = parseInt(this.dataset.autoAdvanceInterval, 10) || 5000;
        this._autoAdvanceDelay = ms;
        this._startAutoAdvance(ms);
      }
    }

    disconnectedCallback() {
      this._stopAutoAdvance();
    }

    // ── public navigation ─────────────────────────────────────────────────

    next(event) {
      const slides = this.slides;
      if (!slides.length) return;
      this._goTo((this._currentIndex + 1) % slides.length);
      this._resetAutoAdvance();
    }

    previous(event) {
      const slides = this.slides;
      if (!slides.length) return;
      this._goTo((this._currentIndex - 1 + slides.length) % slides.length);
      this._resetAutoAdvance();
    }

    // ── nav wiring ────────────────────────────────────────────────────────

    _connectNav() {
      if (this._navWired) return;

      const section = this.closest('.section');
      if (!section) return;

      const prevBtns = section.querySelectorAll('[ref="previous"]');
      const nextBtns = section.querySelectorAll('[ref="next"]');

      if (prevBtns.length === 0 && nextBtns.length === 0) return;

      this._navWired = true;

      prevBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.previous(e);
        });
      });

      nextBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.next(e);
        });
      });

      this._syncNavDisabled(section);
    }

    _syncNavDisabled(sectionEl) {
      const section = sectionEl || this.closest('.section');
      if (!section) return;

      const slides = this.slides;
      const idx = this._currentIndex;

      section.querySelectorAll('[ref="previous"]').forEach((btn) => {
        btn.disabled = !this.infinite && idx <= 0;
      });

      section.querySelectorAll('[ref="next"]').forEach((btn) => {
        btn.disabled = !this.infinite && idx + 1 >= slides.length;
      });

      // Mirror applyHwNavDisabledColorScheme — swap color scheme classes to
      // match the button's disabled state (the nav block renders these as
      // data-enabled-color-scheme / data-disabled-color-scheme on the nav root)
      section.querySelectorAll('.hw-carousel-navigation').forEach((navRoot) => {
        const enabledClass = navRoot.dataset.enabledColorScheme;
        const disabledClass = navRoot.dataset.disabledColorScheme;
        if (!enabledClass && !disabledClass) return;

        navRoot
          .querySelectorAll('.hw-carousel-navigation__control, .slideshow-control')
          .forEach((btn) => {
            if (btn.disabled) {
              if (enabledClass) btn.classList.remove(enabledClass);
              if (disabledClass) {
                btn.classList.add(disabledClass);
                btn.classList.add('hw-carousel-navigation__control--disabled-scheme');
              }
            } else {
              if (disabledClass) {
                btn.classList.remove(disabledClass);
                btn.classList.remove('hw-carousel-navigation__control--disabled-scheme');
              }
              if (enabledClass) btn.classList.add(enabledClass);
            }
          });
      });
    }

    // ── internal ──────────────────────────────────────────────────────────

    _goTo(index, dispatchEvent = true) {
      const slides = this.slides;
      if (!slides.length) return;

      const clamped = Math.max(0, Math.min(index, slides.length - 1));

      slides.forEach((slide, i) => {
        slide.classList.toggle('hw-fade-slider__slide--active', i === clamped);
      });

      this._currentIndex = clamped;
      this._syncNavDisabled();

      if (dispatchEvent) {
        this.dispatchEvent(
          new CustomEvent('slideshow:select', {
            bubbles: true,
            detail: { index: clamped },
          })
        );
      }
    }

    _startAutoAdvance(ms) {
      this._stopAutoAdvance();
      this._autoAdvanceTimer = setInterval(() => {
        const slides = this.slides;
        if (!slides.length) return;
        this._goTo((this._currentIndex + 1) % slides.length);
      }, ms);
    }

    _stopAutoAdvance() {
      if (this._autoAdvanceTimer) {
        clearInterval(this._autoAdvanceTimer);
        this._autoAdvanceTimer = null;
      }
    }

    _resumeAutoAdvance() {
      if (this._autoAdvanceDelay) this._startAutoAdvance(this._autoAdvanceDelay);
    }

    _resetAutoAdvance() {
      if (this._autoAdvanceDelay) {
        this._stopAutoAdvance();
        this._startAutoAdvance(this._autoAdvanceDelay);
      }
    }
  }

  customElements.define('hw-fade-slider', HwFadeSlider);
}

// Stub slideshow-component so the carousel-nav block's whenDefined resolves
// on pages that don't have a carousel-grid section.
if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', class extends HTMLElement {});
}
